import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import TaskView from "../common/TaskView";
import { AcousticTask, AcousticTaskDefinition, SubTask } from "../common/models";
import { randomUUID } from "../../../utils";
import { WordColorStroopTask, TIME_SPENT_PER_ITEM } from "./WordColorStroopTask";
import { translateReactNode } from "../../../../utils/translateReactNode";
import { useStorage } from "../../../../services/StorageProvider";
import { useApiService } from "../../../../services/ApiServiceProvider";
import { WordColorStroopItem } from "./WordColorStroopTypes";
import { i18nLanguageToLabel } from "../../../utils";

const TUTORIAL_URLS: Record<string, string> = {
  'en': 'assets/videos/tutorials/Word_color_Stroop.mp4',
  'es-419': 'assets/videos/tutorials/Word_color_Stroop-es-419.mp4'
}

const Definition: AcousticTaskDefinition = {
  name: 'Word-color Stroop',
  title: 'Word-color Stroop',
  description: '',
  cohort: 'neurology',
  type: 'acoustic-task',
  estimatedCompletionTime: '2:30',
  subTaskDefinitions: [
    {
      name: 'Word-color Stroop',
      instructions: '',
      title: '',
      content: null,
      profileName: 'speech',
      stopwatchSeconds: 75,
      buttonDisabled: async (subTask: SubTask, audioSource?: string) => {
        if (!audioSource || !subTask.stopwatchSeconds) { return true; }

        // get duration from audioSource
        const audio = new Audio(audioSource);
        try {
          const duration = await new Promise<number>((resolve, reject) => { // Duration in seconds
            audio.onloadedmetadata = () => resolve(audio.duration);
            audio.onerror = () => reject(new Error('Failed to load audio'));
          });

          // disable button if duration is less than the stopwatch seconds minus time spent per item (last item hasn't been completed)
          if (duration < subTask.stopwatchSeconds - TIME_SPENT_PER_ITEM) {
            return true;
          }

          return false;
        } catch (error) {
          console.error('Error getting audio duration', error);
          return true;
        } finally {
          audio.onloadedmetadata = null;
          audio.onerror = null;
          audio.src = '';
          audio.load();
        }
      }
    }
  ]
};
export const WordColorStroopDefinition = Definition;

export const WordColorStroop =  ({onCompletion}: {onCompletion: () => void}) => {
  const { i18n } = useTranslation();
  const apiService = useApiService();
  const { storage } = useStorage();
  const session: any = storage.session;
  const [isRecording, setIsRecording] = useState(false);
  const [wordColorStroop, setWordColorStroop] = useState<WordColorStroopItem[]>([]);
  const [task, _] = useState<AcousticTask>(() => {
    return ({
      ...Definition,
      id: randomUUID(),
      startedAt: new Date().toISOString(),
      completedAt: null,
      status: 'pending',
      subTasks: Definition.subTaskDefinitions.map((definition) => ({
        ...definition,
        id: randomUUID(),
        instructions: translateReactNode(definition.instructions, Definition.cohort),
        title: translateReactNode(definition.title, Definition.cohort) as string
      })),
      tutorialUrl: TUTORIAL_URLS[i18n.language] || TUTORIAL_URLS['en']
    });
  });

  const handleOnCompletion = async () => {
    console.log("Audio task completed", task.title);
    onCompletion();
  }

  const handleCreateQuestionnaire = async () => {
    const startAt = task.startedAt || new Date().toISOString();
    const completedAt = task.completedAt || new Date().toISOString();
    const payload = {
      sessionId: session.id,
      subjectId: session.subjectId,
      startedAt: startAt,
      completedAt: completedAt,
      acousticTaskId: task.id,
      items: wordColorStroop,
      language: i18nLanguageToLabel(i18n.language)
    };

    await apiService.createQuestionnaire('/api/wordColorStroopQuestionnaires', payload);
  }

  const handleIsRecordingChange = (isRecording: boolean) => {
    setIsRecording(isRecording);
  };

  const handleWordColorStroopChange = (wordColorStroop: WordColorStroopItem[]) => {
    setWordColorStroop(wordColorStroop);
  };

  return (
    <div>
      <TaskView
        task={task}
        onCompletion={handleOnCompletion}
        onIsRecordingChange={handleIsRecordingChange}
        content={<WordColorStroopTask
          isRecording={isRecording}
          cohort="neurology"
          onWordColorStroopChange={handleWordColorStroopChange}/>}
        createQuestionnaire={handleCreateQuestionnaire}
      />
    </div>
  );
};


